import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";

/**
 * Drop-in replacement for `react-live-clock` (v6.x).
 *
 * The original `react-live-clock` package pulls in `react-moment` →
 * `moment-duration-format`, whose CJS init code (`moment.duration.format = ...`)
 * breaks when Vite's esbuild-based pre-bundler reorders its require() calls.
 * The package also can't be served raw as ESM because it uses `module.exports`.
 *
 * This shim implements the subset of the original API actually used in the app
 * (`format`, `ticking`, `interval`, `timezone`, `date`, `filter`, `onChange`),
 * with `moment-timezone` doing the formatting. All other props are forwarded
 * to the underlying `<time>` element, matching the original.
 */
export default function LiveClock({
  format = "HH:mm",
  ticking = false,
  interval = 1000,
  timezone,
  date,
  children,
  filter = (value) => value,
  onChange,
  ...rest
}) {
  const baseTimeRef = useRef(null);
  const startedAtRef = useRef(null);

  const computeNow = () => {
    const seed = date ?? children;
    if (seed != null) {
      // Mirror react-live-clock: if `date`/`children` is given, treat it as a
      // fixed starting point and let real time advance from there.
      if (baseTimeRef.current == null) {
        baseTimeRef.current = moment(seed);
        startedAtRef.current = moment();
      }
      const drift = moment().diff(startedAtRef.current, "milliseconds");
      const current = baseTimeRef.current.clone().add(drift, "milliseconds");
      if (timezone) current.tz(timezone);
      return filter(current.format(format));
    }
    const m = timezone ? moment.tz(timezone) : moment();
    return filter(m.format(format));
  };

  const [output, setOutput] = useState(computeNow);

  useEffect(() => {
    if (!ticking) return undefined;
    const id = setInterval(() => {
      setOutput((previousOutput) => {
        const next = computeNow();
        if (typeof onChange === "function" && next !== previousOutput) {
          onChange({ output: next, previousOutput });
        }
        return next;
      });
    }, interval);
    return () => clearInterval(id);
  }, [ticking, interval, timezone, format, date, children]);

  return <time {...rest}>{output}</time>;
}
